<template>
    <div class="page-customized">
        <v-progress-linear
            v-if="overlay"
            absolute
            indeterminate
        ></v-progress-linear>

        <app-bar
            :title="event.exists() ? t('event_title_update') : t('event_title_create')"
            back="/events"
        >
            <template #actions>
                <div class="px-10 pt-0">
                    <v-btn-toggle
                        v-model="eventTypeVirtual"
                        mandatory
                        dense
                        active-class="primary"
                    >
                        <v-btn
                            :value="true"
                            class="exs mr-3"
                            small
                        >{{ t('event_label_virtual') }}</v-btn>
                        <v-btn
                            :value="false"
                            class="exs mr-3"
                            small
                        >{{ t('event_label_physical') }}</v-btn>
                    </v-btn-toggle>
                </div>
            </template>
        </app-bar>

        <v-layout class="mt-n8">
            <v-card
                width="100%"
                color="transparent"
                elevation="0"
                class="pb-8"
            >
                <v-card-text class="pb-0">
                    <v-form class="mt-6">
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('event_label_name') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="event.name"
                                    type="text"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('name')"
                                ></v-text-field>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="event.name"
                                    :items="event.translations['name']"
                                    @touch="(v) => event.translations['name'] = v"
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('event_label_foreword') }}</div>
                            <div class="horizontal-input">
                                <v-textarea
                                    v-model="event.foreword"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('foreword')"
                                ></v-textarea>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="event.foreword"
                                    :items="event.translations['foreword']"
                                    @touch="(v) => event.translations['foreword'] = v"
                                    text
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('event_label_description') }}</div>
                            <div class="horizontal-input">
                                <tinymce
                                    key="description"
                                    v-model="event.description"
                                ></tinymce>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="event.description"
                                    :items="event.translations['description']"
                                    @touch="(v) => event.translations['description'] = v"
                                    editor
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div v-if="event.creator !== 'trainee'" class="form-group-horizontal-cols">
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('general_label_created_by') }}</div>
                                <div class="horizontal-input">
                                    <v-select
                                        v-model="event.creator"
                                        :items="communityCreators()"
                                        required
                                        outlined
                                        append-icon="mdi-chevron-down"
                                        dense
                                        :error-messages="form.errors.get('creator')"
                                    ></v-select>
                                </div>
                            </div>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('event_label_associates') }}</div>
                                <div class="horizontal-input">
                                    <v-select
                                        v-model="event.associates"
                                        :items="associates()"
                                        required
                                        outlined
                                        append-icon="mdi-chevron-down"
                                        dense
                                        multiple
                                        :error-messages="form.errors.get('associates')"
                                    ></v-select>
                                </div>
                            </div>
                        </div>
                        <div class="form-group-horizontal-cols">
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('event_label_photo') }}</div>
                                <div class="horizontal-input">
                                    <v-file-input
                                        v-model="event.photo"
                                        required
                                        outlined
                                        prepend-icon=""
                                        prepend-inner-icon="mdi-paperclip"
                                        :append-outer-icon="event.photoUrl ? 'mdi-eye-outline' : ''"
                                        dense
                                        :error-messages="form.errors.get('photo')"
                                        @click:append-outer="viewPhoto(event.photoUrl)"
                                        @click:clear="event.photo = undefined; photoForm.errors.clear('photo')"
                                    ></v-file-input>
                                </div>
                            </div>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('event_label_badges') }}</div>
                                <div class="horizontal-input">
                                    <v-autocomplete
                                        v-model="event.badgeIds"
                                        :items="badges"
                                        required
                                        outlined
                                        append-icon="mdi-chevron-down"
                                        dense
                                        multiple
                                        :error-messages="form.errors.get('badges')"
                                    ></v-autocomplete>
                                </div>
                            </div>
                        </div>
                        <v-switch
                            class="ma-0"
                            v-model="event.hidePhoto"
                            :label="t('event_label_hide_photo')"
                        ></v-switch>
                        <div v-if="! eventTypeVirtual" class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('event_label_location') }}</div>
                            <div class="horizontal-input">
                                <map-autocomplete
                                    v-model="event.location"
                                    @placed="getAddressData"
                                    :error-messages="form.errors.get('location_coordinates')"
                                    :disabled="eventTypeVirtual"
                                />
                            </div>
                        </div>
                        <v-switch
                            class="ma-0"
                            v-model="event.private"
                            :label="t('event_label_private')"
                        ></v-switch>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('event_label_repeat_type') }}</div>
                            <div class="horizontal-input">
                                <v-select
                                    v-model="event.repeatType"
                                    :items="repeatTypes()"
                                    required
                                    outlined
                                    append-icon="mdi-chevron-down"
                                    dense
                                    :disabled="['consistency', 'progression'].includes(event.targetType)"
                                    :error-messages="form.errors.get('repeat_type')"
                                ></v-select>
                            </div>
                        </div>
                        <div
                            v-if="event.repeatType && event.repeatType !== 'one_time'"
                            class="form-group-horizontal"
                        >
                            <div class="horizontal-label">{{ t('event_label_repeat_count') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="event.repeatCount"
                                    v-maska="'######'"
                                    type="number"
                                    step="1"
                                    min="2"
                                    required
                                    outlined
                                    dense
                                    :disabled="['consistency', 'progression'].includes(event.targetType)"
                                ></v-text-field>
                            </div>
                        </div>
                        <div class="form-group-horizontal-cols">
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('event_label_start_at') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="startAt"
                                        type="datetime-local"
                                        required
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('start_at')"
                                    ></v-text-field>
                                </div>
                            </div>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('event_label_end_at') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="endAt"
                                        type="datetime-local"
                                        required
                                        outlined
                                        dense
                                        :disabled="['consistency', 'progression'].includes(event.targetType)"
                                        :error-messages="form.errors.get('end_at')"
                                    ></v-text-field>
                                </div>
                            </div>
                        </div>
                        <div class="form-group-horizontal" v-if="eventTypeVirtual">
                            <div class="horizontal-label">{{ t('event_label_goal') }}</div>
                            <div class="horizontal-input">
                                <v-select
                                    v-model="event.targetType"
                                    :items="targetTypes()"
                                    required
                                    outlined
                                    append-icon="mdi-chevron-down"
                                    dense
                                    :error-messages="form.errors.get('target_type')"
                                ></v-select>
                            </div>
                        </div>
                        <template v-if="event.targetType && event.targetType !== 'none'">
                            <div class="form-group-horizontal" v-if="['distance', 'distance_shared', 'distance_session'].includes(event.targetType)">
                                <div class="horizontal-label">{{ t('event_label_distance') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="event.distance"
                                        v-maska="'######'"
                                        type="number"
                                        step="1"
                                        required
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('distance')"
                                    ></v-text-field>
                                </div>
                            </div>
                            <div class="form-group-horizontal" v-if="['duration', 'duration_shared', 'duration_session'].includes(event.targetType)">
                                <div class="horizontal-label">{{ t('event_label_duration') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="event.duration"
                                        required
                                        outlined
                                        dense
                                        v-maska="'###:##:##.###'"
                                        :error-messages="form.errors.get('duration')"
                                    ></v-text-field>
                                </div>
                            </div>
                            <div class="form-group-horizontal" v-if="['workout_number', 'workout_open_water_number'].includes(event.targetType)">
                                <div class="horizontal-label">{{ t('event_label_workout_numbers') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="event.workoutNumbers"
                                        v-maska="'######'"
                                        type="number"
                                        step="1"
                                        required
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('workout_number')"
                                    ></v-text-field>
                                </div>
                            </div>
                            <div class="form-group-horizontal-cols" v-if="['min_train', 'min_train_consecutive'].includes(event.targetType)">
                                <div class="form-group-horizontal">
                                    <div class="horizontal-label">{{ t('event_label_duration') }}</div>
                                    <div class="horizontal-input">
                                        <v-text-field
                                            v-model="event.duration"
                                            required
                                            outlined
                                            dense
                                            v-maska="'###:##:##.###'"
                                            :error-messages="form.errors.get('duration')"
                                        ></v-text-field>
                                    </div>
                                </div>
                                <div class="form-group-horizontal">
                                    <div class="horizontal-label">{{ t('event_label_period_numbers') }}</div>
                                    <div class="horizontal-input">
                                        <v-text-field
                                            v-model="event.periodNumbers"
                                            type="number"
                                            min="0"
                                            step="1"
                                            required
                                            outlined
                                            dense
                                            :error-messages="form.errors.get('period_number')"
                                        ></v-text-field>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group-horizontal-cols" v-if="['consistency', 'progression'].includes(event.targetType)">
                                <div class="form-group-horizontal">
                                    <div class="horizontal-label">{{ t('event_label_target_metadata_period') }}</div>
                                    <div class="horizontal-input">
                                        <v-select
                                            v-model="event.targetMetadata.period"
                                            :items="eventTargetPeriods()"
                                            required
                                            outlined
                                            append-icon="mdi-chevron-down"
                                            dense
                                            :error-messages="form.errors.get('target_metadata.period')"
                                        ></v-select>
                                    </div>
                                </div>
                                <div class="form-group-horizontal">
                                    <div class="horizontal-label">{{ t('event_label_period_numbers') }}</div>
                                    <div class="horizontal-input">
                                        <v-text-field
                                            v-model="event.targetMetadata.period_numbers"
                                            type="number"
                                            min="0"
                                            step="1"
                                            required
                                            outlined
                                            dense
                                            @keydown="preventNegative"
                                            :error-messages="form.errors.get('target_metadata.period_numbers')"
                                        ></v-text-field>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group-horizontal" v-if="['consistency'].includes(event.targetType)">
                                <div class="horizontal-label">{{ t('event_label_workout_numbers') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="event.targetMetadata.workout_numbers"
                                        type="number"
                                        min="1"
                                        step="1"
                                        required
                                        outlined
                                        dense
                                        @keydown="preventNegative"
                                        :error-messages="form.errors.get('target_metadata.workout_numbers')"
                                    ></v-text-field>
                                </div>
                            </div>
                            <template v-if="['progression'].includes(event.targetType)">
                                <div class="form-group-horizontal-cols">
                                    <div class="form-group-horizontal">
                                        <div class="horizontal-label">{{ t('event_label_target_metadata_target') }}</div>
                                        <div class="horizontal-input">
                                            <v-select
                                                v-model="event.targetMetadata.target"
                                                :items="eventTargets()"
                                                required
                                                outlined
                                                append-icon="mdi-chevron-down"
                                                dense
                                                :error-messages="form.errors.get('target_metadata.target')"
                                            ></v-select>
                                        </div>
                                    </div>
                                    <div class="form-group-horizontal">
                                        <div class="horizontal-label">{{ t('event_label_target_metadata_mode') }}</div>
                                        <div class="horizontal-input">
                                            <v-select
                                                v-model="event.targetMetadata.mode"
                                                :items="eventTargetModes()"
                                                required
                                                outlined
                                                append-icon="mdi-chevron-down"
                                                dense
                                                :error-messages="form.errors.get('target_metadata.mode')"
                                            ></v-select>
                                        </div>
                                    </div>
                                </div>
                                <div class="form-group-horizontal-cols">
                                    <div class="form-group-horizontal">
                                        <div class="horizontal-label">{{ t('event_label_target_metadata_start') }}</div>
                                        <div class="horizontal-input">
                                            <v-text-field
                                                key="event_target_start_duration"
                                                v-if="event.targetMetadata.target === 'duration'"
                                                v-model="event.targetMetadata.start"
                                                required
                                                outlined
                                                dense
                                                v-maska="'###:##:##.###'"
                                                :error-messages="form.errors.get('target_metadata.start')"
                                            ></v-text-field>
                                            <v-text-field
                                                key="event_target_start_distance"
                                                v-else
                                                v-model="event.targetMetadata.start"
                                                type="number"
                                                min="0"
                                                step="1"
                                                required
                                                outlined
                                                dense
                                                @keydown="preventNegative"
                                                :error-messages="form.errors.get('target_metadata.start')"
                                            ></v-text-field>
                                        </div>
                                    </div>
                                    <div class="form-group-horizontal">
                                        <div class="horizontal-label">{{ t('event_label_target_metadata_increment') }}</div>
                                        <div class="horizontal-input">
                                            <v-text-field
                                                key="event_target_increment_duration"
                                                v-if="event.targetMetadata.target === 'duration' && event.targetMetadata.mode === 'manual'"
                                                v-model="event.targetMetadata.increment"
                                                required
                                                outlined
                                                dense
                                                v-maska="'###:##:##.###'"
                                                :error-messages="form.errors.get('target_metadata.increment')"
                                            ></v-text-field>

                                            <v-text-field
                                                key="event_target_increment_number"
                                                v-else
                                                v-model.number="event.targetMetadata.increment"
                                                type="number"
                                                min="0"
                                                step="1"
                                                required
                                                outlined
                                                dense
                                                @keydown="preventNegative"
                                                :error-messages="form.errors.get('target_metadata.increment')"
                                            ></v-text-field>
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <div class="form-group-horizontal" v-if="eventTypeVirtual">
                                <div class="horizontal-label">{{ t('event_label_stroke') }}</div>
                                <div class="horizontal-input">
                                    <v-select
                                        v-model="event.strokeId"
                                        :items="strokes"
                                        required
                                        outlined
                                        append-icon="mdi-chevron-down"
                                        dense
                                        :error-messages="form.errors.get('stroke_id')"
                                    ></v-select>
                                </div>
                            </div>
                        </template>
                        <div class="form-group-horizontal" v-if="eventTypeVirtual">
                            <div class="horizontal-label">{{ t('event_label_reward') }}</div>
                            <div class="horizontal-input">
                                <v-textarea
                                    v-model="event.reward"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('reward')"
                                ></v-textarea>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="event.reward"
                                    :items="event.translations['reward']"
                                    @touch="(v) => event.translations['reward'] = v"
                                    text
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal" v-if="eventTypeVirtual">
                            <div class="horizontal-label">{{ t('event_label_reward_photo') }}</div>
                            <div class="horizontal-input">
                                <v-file-input
                                    v-model="event.rewardPhoto"
                                    required
                                    outlined
                                    prepend-icon=""
                                    prepend-inner-icon="mdi-paperclip"
                                    :append-outer-icon="event.rewardPhotoUrl ? 'mdi-eye-outline' : ''"
                                    dense
                                    :error-messages="form.errors.get('reward_photo')"
                                    @click:append-outer="viewPhoto(event.rewardPhotoUrl)"
                                    @click:clear="event.photo = undefined; photoForm.errors.clear('reward_photo')"
                                ></v-file-input>
                            </div>
                        </div>
                        <div class="form-group-horizontal" v-if="event.photos && event.photos.length">
                            <v-sheet
                                v-for="(photo, i) in event.photos"
                                :key="photo"
                                color="white"
                                elevation="0"
                                height="200"
                                width="200"
                                class="float-left mr-8 mb-8 bordered position-relative"
                            >
                                <v-img
                                    :lazy-src="photo"
                                    :src="photo"
                                    max-height="150"
                                    max-width="150"
                                    class="mx-auto mt-6"
                                ></v-img>
                                <confirm-modal
                                    :title="t('file_manager_label_confirm_delete')"
                                    @agree="event.delPhoto(i)"
                                >
                                    <template #default="{ on, attrs }">
                                        <v-btn
                                            plain
                                            icon
                                            absolute
                                            top
                                            right
                                            class="mt-n5 mr-n5"
                                            v-on="on"
                                            v-bind="attrs"
                                        ><v-icon color="customized-red">mdi-close</v-icon></v-btn>
                                    </template>
                                </confirm-modal>
                                <v-btn
                                    v-if="i !== 0"
                                    @click="event.updatePhotoNumber(i, (i - 1))"
                                    plain
                                    icon
                                    absolute
                                    bottom
                                    left
                                    class="mb-n5 ml-n5"
                                ><v-icon color="customized-primary">mdi-arrow-left</v-icon></v-btn>
                                <v-btn
                                    v-if="i !== event.photos.length - 1"
                                    @click="event.updatePhotoNumber(i, (i + 1))"
                                    plain
                                    icon
                                    absolute
                                    bottom
                                    right
                                    class="mb-n5 mr-n5"
                                ><v-icon color="customized-primary">mdi-arrow-right</v-icon></v-btn>
                            </v-sheet>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('event_label_photos') }}</div>
                            <div class="horizontal-input">
                                <v-file-input
                                    v-model="event.photoUps"
                                    required
                                    outlined
                                    prepend-icon=""
                                    prepend-inner-icon="mdi-paperclip"
                                    dense
                                    multiple
                                    :placeholder="t('file_manager_label_browse_files')"
                                    :error-messages="photoForm.errors.get('photos.0') || photoForm.errors.get('photos')"
                                    @click:clear="event.photoUps = undefined; photoForm.errors.clear('photos.0'); photoForm.errors.clear('photos')"
                                ></v-file-input>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('event_label_registration_url') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="event.registrationUrl"
                                    type="text"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('registration_url')"
                                ></v-text-field>
                            </div>
                        </div>
                        <v-switch
                            class="ma-0"
                            v-model="event.hideUsers"
                            :label="t('event_label_hide_users')"
                        ></v-switch>
                        <v-switch
                            class="ma-0"
                            v-model="event.hideRanking"
                            :label="t('event_label_hide_ranking')"
                        ></v-switch>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('event_label_attendees') }}</div>
                            <div class="horizontal-input">
                                <user-search-modal
                                    v-model="event"
                                >
                                    <template #default="{ on, attrs }">
                                        <v-btn
                                            class="exs mt-1"
                                            color="primary"
                                            small
                                            v-on="on"
                                            v-bind="attrs"
                                        >{{ t('general_action_add_new') }}</v-btn>
                                    </template>
                                </user-search-modal>
                            </div>
                        </div>
                        <template v-if="event.participants.length">
                            <v-btn
                                @click="event.participants = []"
                                v-ripple="false"
                                plain
                                color="orange darken-4"
                                style="font-size: 12px; margin: 0 0 0 314px"
                            >{{ t('general_action_clear_all') }}</v-btn>
                            <v-card class="app-card app-card-bordered">
                                <v-card-text>
                                    <v-list>
                                        <v-list-item
                                            v-for="participant in event.participants"
                                            :key="`participant_${participant.id}`"
                                        >
                                            {{ participant.fullName }} | {{ participant.id }}
                                            <v-spacer></v-spacer>
                                            <v-btn
                                                @click="detach(participant)"
                                                v-ripple="false"
                                                plain
                                                color="orange darken-4"
                                                style="font-size: 12px"
                                            >{{ t('general_action_remove') }}</v-btn>
                                        </v-list-item>
                                    </v-list>
                                </v-card-text>
                            </v-card>
                        </template>
                    </v-form>
                </v-card-text>

                <v-card-actions class="px-4 pt-8">
                    <v-btn
                        :to="{ name: 'events' }"
                        class="mr-4"
                        color="primary"
                    >{{ t('general_action_cancel') }}</v-btn>
                    <v-btn
                        :loading="form.busy"
                        :disabled="form.busy"
                        color="orange"
                        @click.prevent="submit"
                    >{{ event.exists() ? t('general_action_save') : t('event_title_create') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useForm } from '@/core/form'
    import { useEnums } from '@/core/defaults'
    import { User } from '@/core/models/user'
    import { Event } from '@/core/models/event'
    import { Stroke } from '@/core/models/stroke'
    import { Badge } from '@/core/models/badge'
    import moment from 'moment/moment'
    import EditTranslationsModal from '@/components/EditTranslationsModal.vue'
    import MapAutocomplete from '@/components/MapAutocomplete.vue'
    import UserSearchModal from '@/components/events/UserSearchModal.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import Tinymce from '@/components/Tinymce.vue'

    export default defineComponent({
        components: {
            EditTranslationsModal,
            MapAutocomplete,
            UserSearchModal,
            ConfirmModal,
            Tinymce,
        },

        /**
         * Component constructor.
         *
         * @param props
         */
        setup(props) {
            const store = useStore()
            const router = useRouter()
            const id = router.currentRoute.params['id'] || undefined
            const captured = router.currentRoute.query['id'] || undefined

            const enums = useEnums()
            const i18n = useI18n()

            const eventModel = computed(() => <Event>store.getters['event/get'])

            const state = reactive({
                overlay: true,
                eventTypeVirtual: true,
                userSearchModal: false,
                startAt: <string | undefined>undefined,
                endAt: <string | undefined>undefined,
            })


            const strokes = computed(() => [ { text: '-', value: null } ].concat(
                store.getters['strokeCollection/get'].data.map((item: Stroke) => {
                    return { text: item.name, value: item.id }
                })
            ))

            const badges = computed(() => store.getters['badgeCollection/get'].data.map((item: Badge) => {
                return {text: item.name, value: item.id}
            }))

            const capture = () => {
                store.dispatch('badgeCollection/get', { sort: 'sort_order' })
                store.dispatch(
                    'event/getOrNew',
                    {
                        id: (id || captured),
                        payload: { append: 'translations', relations: 'schedules,participants,badges' }
                    }).then(() => {
                        state.overlay = false
                        state.eventTypeVirtual = eventModel.value.eventType === 'virtual'
                        state.startAt = eventModel.value.startAt?.format('YYYY-MM-DDTHH:mm')
                        state.endAt = eventModel.value.endAt?.format('YYYY-MM-DDTHH:mm')
                        if (captured) {
                            eventModel.value.enabled = false
                            eventModel.value.name = eventModel.value.name + ' - copy'
                        }
                    })
            }

            const form = reactive(useForm())
            const photoForm = reactive(useForm())

            const submit = () => {
                if (captured) {
                    eventModel.value.id = <any>undefined
                }
                form.usingSubmit(store.dispatch('event/updateOrCreate', {
                    payload: eventModel.value.payload()
                }))
                    .then(() => {
                        const id = <any>eventModel.value.id
                        const next = () => router.push({ name: 'events.view', params: <any>{ id: id } })
                        store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                        store.dispatch('userCollection/clear')
                        if (eventModel.value.photo || eventModel.value.photos) {
                            const data = new FormData()
                            if (eventModel.value.photo) {
                                data.append('photo', eventModel.value.photo)
                            }
                            if (eventModel.value.rewardPhoto) {
                                data.append('reward_photo', eventModel.value.rewardPhoto)
                            }
                            if (eventModel.value.photoUps) {
                                eventModel.value.photoUps.map(i => data.append('photos[]', i))
                            }
                            photoForm.usingSubmit(
                                store.dispatch('event/photo', { id: id, payload: data }).then(() => { next() })
                            ).catch(() => {
                                store.dispatch('notify', { color: 'error', message: i18n.t('general_notice_upload_failed') })
                            })
                        } else {
                            next()
                        }
                    })
            }

            const viewPhoto = (url) => {
                window.open(url)
            }

            const detach = (user: User) => {
                eventModel.value.participants = eventModel.value.participants.filter(u => u.id !== user.id)
            }

            const getAddressData = (address: any) => {
                eventModel.value.location = address?.formatted_address || null
                eventModel.value.locationCoordinates = address ? {
                    latitude: address.lat,
                    longitude: address.lon
                } : null
            }

            const preventNegative = (e: KeyboardEvent) => {
                if (['-', '+', 'e', 'E'].includes(e.key)) {
                    e.preventDefault()
                }
            }

            watch(() => state.eventTypeVirtual, (newValue) => {
                eventModel.value.eventType = state.eventTypeVirtual ? 'virtual' : 'physical'
                if (state.eventTypeVirtual) {
                    eventModel.value.location = null
                    eventModel.value.locationCoordinates = null
                }
            })

            watch(() => [state.startAt], (newValue: any) => {
                eventModel.value.startAt = moment(newValue, moment.HTML5_FMT.DATETIME_LOCAL)
            })

            watch(() => [state.endAt], (newValue: any) => {
                eventModel.value.endAt = moment(newValue, moment.HTML5_FMT.DATETIME_LOCAL)
            })

            watch(() => [eventModel.value.repeatType], (newValue: any) => {
                if (eventModel.value.repeatType === 'one_time') {
                    eventModel.value.repeatCount = 1
                } else if (eventModel.value.repeatCount === 1) {
                    eventModel.value.repeatCount = 10
                }
            })

            watch(() => eventModel.value.targetMetadata.target, (newTarget: any) => {
                const meta = eventModel.value.targetMetadata
                // 1. START processing (depends only on Target)
                if (newTarget === 'duration') {
                    if (typeof meta.start !== 'string' || ! meta.start) {
                        meta.start = '000:00:00.000'
                    }
                } else {
                    if (typeof meta.start !== 'number' || ! meta.start) {
                        meta.start = 1
                    }
                }
                // 2. INCREMENT processing (depends on Target AND Mode)
                if (newTarget === 'duration' && meta.mode === 'manual') {
                    if (typeof meta.increment !== 'string' || !meta.increment) {
                        meta.increment = '000:00:00.000'
                    }
                } else {
                    if (typeof meta.increment !== 'number' || !meta.increment) {
                        meta.increment = 1
                    }
                }
            })

            watch(() => eventModel.value.targetMetadata.mode, (newMode: any) => {
                const meta = eventModel.value.targetMetadata
                if (meta.target === 'duration' && newMode === 'manual') {
                    if (typeof meta.increment !== 'string' || ! meta.increment) {
                        meta.increment = '000:00:00.000'
                    }
                } else {
                    if (typeof meta.increment !== 'number' || ! meta.increment) {
                        meta.increment = 1
                    }
                }
            })

            onMounted(() => {
                store.dispatch('strokeCollection/get', { sort: 'sort_order', type: 'primary' })
                capture()
            })

            return {
                form,
                photoForm,
                capture,
                viewPhoto,
                detach,
                submit,
                event: eventModel.value,
                strokes,
                badges,
                getAddressData,
                preventNegative,
                moment,
                ...enums,
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
